import axios from "axios";
import config from "../config";
import { resolveServerRoot } from "../helpers/api_origin";
import {
  AuthUserPayload,
  clearStoredAuth,
  getStoredAuth,
  setStoredAuth,
} from "../helpers/auth_storage";
import {
  clearIdentitySession,
  getIdentitySession,
  setIdentitySession,
  setLastTenantId,
  StudioMembership,
  syncPreferredTenantWithMemberships,
} from "../helpers/identity_storage";
import {
  clearImpersonationContext,
  getImpersonationContext,
  setImpersonationContext,
} from "../helpers/impersonation_storage";
import {
  clearStoredSuperAdminAuth,
  getStoredSuperAdminAuth,
  setStoredSuperAdminAuth,
  SuperAdminAuthUserPayload,
} from "../helpers/super_admin_auth_storage";
import i18n from "../i18n";
import { ApiResponse } from "../types/api";
import { toast } from "react-toastify";

const notifyPreferredTenantCleared = (
  result: ReturnType<typeof syncPreferredTenantWithMemberships>
): void => {
  if (!result.cleared) {
    return;
  }

  const name = result.tenantName?.trim();

  toast.info(
    name
      ? i18n.t("auth.switch_studio.preferred_cleared_named", { name })
      : i18n.t("auth.switch_studio.preferred_cleared")
  );
};

export interface AuthTokens {
  type?: "tenant_token";
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  membership?: StudioMembership;
  tenant?: { id: number; name: string };
}

export interface MembershipSelectionPayload {
  type: "membership_selection_required";
  identity_token: string;
  identity_token_expires_in: number;
  email?: string;
  name?: string | null;
  memberships: StudioMembership[];
  last_tenant_id?: number | null;
}

export interface GoogleSignupPayload {
  type: "google_signup_required";
  identity_token: string;
  identity_token_expires_in: number;
  email: string;
  name: string | null;
}

export interface StudioCreationPayload {
  type: "studio_creation_required";
  identity_token: string;
  identity_token_expires_in: number;
  email: string;
  name: string | null;
}

export type LoginResult =
  | AuthTokens
  | MembershipSelectionPayload
  | StudioCreationPayload;
export type GoogleAuthResult =
  | AuthTokens
  | MembershipSelectionPayload
  | GoogleSignupPayload
  | StudioCreationPayload;

export interface RegisterPayload {
  studio_name: string;
  name: string;
  email: string;
  password?: string;
  password_confirmation?: string;
  email_challenge_id?: string;
  email_code?: string;
  identity_token?: string;
  plan_intent?: "solo" | "studio" | "multi" | "free";
  referral_code?: string;
}

export interface SuperAdminAuthTokens {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface SuperAdminOtpChallenge {
  requires_otp: true;
  challenge_id: string;
  expires_in: number;
  email_hint: string;
}

export type SuperAdminLoginResult =
  | SuperAdminAuthTokens
  | SuperAdminOtpChallenge;

export interface ImpersonationTokens {
  access_token: string;
  token_type: string;
  expires_in: number;
}

const { api: apiConfig } = config;

const LANGUAGE_STORAGE_KEY = "I18N_LANGUAGE";

const resolveAcceptLanguage = (): string => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);

  if (stored) {
    return stored === "sp" ? "es" : stored;
  }

  return i18n.language || "pt-BR";
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");

    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );

    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
};

const buildAuthPayload = (tokens: AuthTokens): AuthUserPayload => ({
  token: tokens.access_token,
  refreshToken: tokens.refresh_token,
  user: decodeJwtPayload(tokens.access_token),
});

const buildSuperAdminAuthPayload = (
  tokens: SuperAdminAuthTokens
): SuperAdminAuthUserPayload => ({
  token: tokens.access_token,
  user: decodeJwtPayload(tokens.access_token),
});

const authAxios = axios.create({
  baseURL: apiConfig.API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

authAxios.interceptors.request.use((requestConfig) => {
  requestConfig.headers["Accept-Language"] = resolveAcceptLanguage();

  const stored = getStoredAuth();

  if (stored?.token) {
    requestConfig.headers.Authorization = `Bearer ${stored.token}`;
  }

  return requestConfig;
});

export const superAdminApi = axios.create({
  baseURL: resolveServerRoot(),
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

superAdminApi.interceptors.request.use((requestConfig) => {
  requestConfig.headers["Accept-Language"] = resolveAcceptLanguage();

  const stored = getStoredSuperAdminAuth();

  if (stored?.token) {
    requestConfig.headers.Authorization = `Bearer ${stored.token}`;
  }

  return requestConfig;
});

const persistTenantSession = (tokens: AuthTokens): void => {
  setStoredAuth(buildAuthPayload(tokens));
  clearIdentitySession();

  const tenantId = tokens.membership?.tenant_id ?? tokens.tenant?.id;

  if (typeof tenantId === "number") {
    setLastTenantId(tenantId);
  }
};

export const login = async (
  email: string,
  password: string
): Promise<ApiResponse<LoginResult>> => {
  const response = await authAxios.post<ApiResponse<LoginResult>>("/login", {
    email,
    password,
  });

  return applyLoginResult(response.data);
};

export const requestEmailCode = async (
  email: string,
  purpose: "login" | "signup" | "invite_accept" = "login"
): Promise<
  ApiResponse<{ challenge_id: string; expires_in: number; email_hint: string }>
> => {
  const response = await authAxios.post<
    ApiResponse<{ challenge_id: string; expires_in: number; email_hint: string }>
  >("/auth/email/request-code", { email, purpose });

  return response.data;
};

export const verifyEmailCode = async (
  challengeId: string,
  code: string
): Promise<ApiResponse<LoginResult>> => {
  const response = await authAxios.post<ApiResponse<LoginResult>>(
    "/auth/email/verify-code",
    {
      challenge_id: challengeId,
      code,
    }
  );

  return applyLoginResult(response.data);
};

export const getGoogleAuthorizationUrl = async (): Promise<
  ApiResponse<{ authorization_url: string }>
> => {
  const response = await authAxios.get<
    ApiResponse<{ authorization_url: string }>
  >("/auth/google/redirect");

  return response.data;
};

export const exchangeGoogleCode = async (
  code: string,
  intent: "login" | "signup" = "login"
): Promise<ApiResponse<GoogleAuthResult>> => {
  const response = await authAxios.post<ApiResponse<GoogleAuthResult>>(
    "/auth/google/callback",
    { code, intent }
  );

  if (
    response.data.success &&
    response.data.data &&
    response.data.data.type !== "google_signup_required"
  ) {
    return applyLoginResult(
      response.data as ApiResponse<LoginResult>
    ) as ApiResponse<GoogleAuthResult>;
  }

  return response.data;
};

export const linkGoogleAccount = async (
  code: string
): Promise<ApiResponse<{ google_linked: boolean; google_email: string | null }>> => {
  const response = await authAxios.post<
    ApiResponse<{ google_linked: boolean; google_email: string | null }>
  >("/auth/google/link", { code });

  return response.data;
};

export const unlinkGoogleAccount = async (): Promise<
  ApiResponse<{ google_linked: boolean; google_email: null }>
> => {
  const response = await authAxios.delete<
    ApiResponse<{ google_linked: boolean; google_email: null }>
  >("/auth/google/link");

  return response.data;
};

const applyLoginResult = (
  body: ApiResponse<LoginResult>
): ApiResponse<LoginResult> => {
  if (!body.success || !body.data) {
    return body;
  }

  if (body.data.type === "membership_selection_required") {
    const expiresIn = body.data.identity_token_expires_in || 300;
    setIdentitySession({
      identityToken: body.data.identity_token,
      memberships: body.data.memberships || [],
      expiresAt: new Date(Date.now() + expiresIn * 1000).toISOString(),
      lastTenantId: body.data.last_tenant_id ?? null,
      email: body.data.email ?? null,
      name: body.data.name ?? null,
    });
    notifyPreferredTenantCleared(
      syncPreferredTenantWithMemberships(body.data.memberships || [])
    );
    clearStoredAuth();
    return body;
  }

  if (body.data.type === "studio_creation_required") {
    clearStoredAuth();
    return body;
  }

  if ("access_token" in body.data && body.data.access_token) {
    persistTenantSession(body.data);
  }

  return body;
};

export const selectTenant = async (
  tenantId: number,
  identityToken?: string
): Promise<ApiResponse<AuthTokens>> => {
  const token = identityToken || getIdentitySession()?.identityToken || "";

  const response = await authAxios.post<ApiResponse<AuthTokens>>(
    "/login/select-tenant",
    {
      tenant_id: tenantId,
      identity_token: token,
    },
    {
      headers: token ? { "X-Identity-Token": token } : undefined,
    }
  );

  const body = response.data;

  if (body.success && body.data?.access_token) {
    persistTenantSession(body.data);
  }

  return body;
};

export const switchTenant = async (
  tenantId: number
): Promise<ApiResponse<AuthTokens>> => {
  const stored = getStoredAuth();

  const response = await authAxios.post<ApiResponse<AuthTokens>>(
    "/switch-tenant",
    {
      tenant_id: tenantId,
      refresh_token: stored?.refreshToken || "",
    }
  );

  const body = response.data;

  if (body.success && body.data?.access_token) {
    persistTenantSession(body.data);
  }

  return body;
};

export const getMemberships = async (): Promise<
  ApiResponse<{ current_tenant_id: number | null; memberships: StudioMembership[] }>
> => {
  const identitySession = getIdentitySession();
  const headers: Record<string, string> = {};

  if (identitySession?.identityToken) {
    headers["X-Identity-Token"] = identitySession.identityToken;
  }

  const response = await authAxios.get<
    ApiResponse<{ current_tenant_id: number | null; memberships: StudioMembership[] }>
  >("/me/memberships", { headers });

  if (response.data.success && response.data.data?.memberships) {
    notifyPreferredTenantCleared(
      syncPreferredTenantWithMemberships(response.data.data.memberships)
    );
  }

  return response.data;
};

type MembershipActionResult = {
  membership: StudioMembership;
  memberships: StudioMembership[];
} & Partial<AuthTokens>;

const membershipActionHeaders = (): Record<string, string> | undefined => {
  const token = getIdentitySession()?.identityToken;

  return token ? { "X-Identity-Token": token } : undefined;
};

export const acceptMembership = async (
  membershipId: number
): Promise<ApiResponse<MembershipActionResult>> => {
  const identityToken = getIdentitySession()?.identityToken || "";

  const response = await authAxios.post<ApiResponse<MembershipActionResult>>(
    `/memberships/${membershipId}/accept`,
    { identity_token: identityToken },
    { headers: membershipActionHeaders() }
  );

  const body = response.data;

  if (body.success && body.data?.access_token) {
    persistTenantSession(body.data as AuthTokens);
  } else if (body.success && body.data?.memberships && identityToken) {
    const current = getIdentitySession();
    if (current) {
      setIdentitySession({
        ...current,
        memberships: body.data.memberships,
      });
    }
  }

  if (body.success && body.data?.memberships) {
    notifyPreferredTenantCleared(
      syncPreferredTenantWithMemberships(body.data.memberships)
    );
  }

  return body;
};

export const completeInviteSetup = async (payload: {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
  name?: string;
}): Promise<ApiResponse<LoginResult>> => {
  const response = await authAxios.post<ApiResponse<LoginResult>>(
    "/auth/invite/setup-password",
    payload
  );

  const body = response.data;

  if (
    body.success &&
    body.data &&
    "type" in body.data &&
    body.data.type === "membership_selection_required"
  ) {
    setIdentitySession({
      identityToken: body.data.identity_token,
      expiresAt: new Date(
        Date.now() + body.data.identity_token_expires_in * 1000
      ).toISOString(),
      memberships: body.data.memberships,
      lastTenantId: body.data.last_tenant_id ?? null,
      email: body.data.email ?? null,
      name: body.data.name ?? null,
    });
  } else if (body.success && body.data && "access_token" in body.data) {
    persistTenantSession(body.data);
  }

  return body;
};

export const declineMembership = async (
  membershipId: number
): Promise<ApiResponse<MembershipActionResult>> => {
  const identityToken = getIdentitySession()?.identityToken || "";

  const response = await authAxios.post<ApiResponse<MembershipActionResult>>(
    `/memberships/${membershipId}/decline`,
    { identity_token: identityToken },
    { headers: membershipActionHeaders() }
  );

  const body = response.data;

  if (body.success && body.data?.memberships && identityToken) {
    const current = getIdentitySession();
    if (current) {
      setIdentitySession({
        ...current,
        memberships: body.data.memberships,
      });
    }
  }

  if (body.success && body.data?.memberships) {
    notifyPreferredTenantCleared(
      syncPreferredTenantWithMemberships(body.data.memberships)
    );
  }

  return body;
};

export const superAdminLogin = async (
  email: string,
  password: string
): Promise<ApiResponse<SuperAdminLoginResult>> => {
  const response = await superAdminApi.post<ApiResponse<SuperAdminLoginResult>>(
    "/super-admin/login",
    {
      email,
      password,
    }
  );

  const body = response.data;

  if (
    body.success &&
    body.data &&
    "access_token" in body.data &&
    body.data.access_token
  ) {
    setStoredSuperAdminAuth(buildSuperAdminAuthPayload(body.data));
  }

  return body;
};

export const superAdminVerifyOtp = async (
  challengeId: string,
  code: string
): Promise<ApiResponse<SuperAdminAuthTokens>> => {
  const response = await superAdminApi.post<ApiResponse<SuperAdminAuthTokens>>(
    "/super-admin/login/verify-otp",
    {
      challenge_id: challengeId,
      code,
    }
  );

  const body = response.data;

  if (body.success && body.data?.access_token) {
    setStoredSuperAdminAuth(buildSuperAdminAuthPayload(body.data));
  }

  return body;
};

export const superAdminUpdatePassword = async (payload: {
  current_password: string;
  password: string;
  password_confirmation: string;
}): Promise<ApiResponse<Record<string, never>>> => {
  const response = await superAdminApi.patch<
    ApiResponse<Record<string, never>>
  >("/super-admin/me/password", payload);

  return response.data;
};

export const register = async (
  payload: RegisterPayload
): Promise<ApiResponse<AuthTokens & { tenant?: { id: number; name: string } }>> => {
  const response = await authAxios.post<
    ApiResponse<AuthTokens & { tenant?: { id: number; name: string } }>
  >("/register", payload);

  const body = response.data;

  if (body.success && body.data?.access_token) {
    setStoredAuth(buildAuthPayload(body.data));
  }

  return body;
};

export const forgotPassword = async (email: string): Promise<ApiResponse<Record<string, never>>> => {
  const response = await authAxios.post<ApiResponse<Record<string, never>>>(
    "/auth/forgot-password",
    { email }
  );

  return response.data;
};

export const refresh = async (
  refreshToken: string
): Promise<ApiResponse<AuthTokens>> => {
  const response = await authAxios.post<ApiResponse<AuthTokens>>("/refresh", {
    refresh_token: refreshToken,
  });

  const body = response.data;

  if (body.success && body.data?.access_token) {
    setStoredAuth(buildAuthPayload(body.data));
  }

  return body;
};

export const logout = async (): Promise<void> => {
  const stored = getStoredAuth();

  try {
    if (stored?.token) {
      await authAxios.post("/logout");
    }
  } catch {
    // Best-effort server logout; local session is always cleared.
  } finally {
    clearStoredAuth();
    clearIdentitySession();
  }
};

export const superAdminLogout = async (): Promise<void> => {
  clearStoredSuperAdminAuth();
};

export const getAuthSession = (): AuthUserPayload | null => getStoredAuth();

export const getAuthRoleNames = (): string[] => {
  const stored = getStoredAuth();
  const roles = stored?.user?.roles;

  if (!Array.isArray(roles)) {
    return [];
  }

  return roles.map(String);
};

export const userHasAnyRole = (allowedRoles: string[]): boolean => {
  const userRoles = getAuthRoleNames();

  return allowedRoles.some((role) => userRoles.includes(role));
};

export const getSuperAdminAuthSession = (): SuperAdminAuthUserPayload | null =>
  getStoredSuperAdminAuth();

export const startImpersonation = (
  tokens: ImpersonationTokens,
  tenantName: string
): void => {
  const superAdminBackup = getStoredSuperAdminAuth();

  if (!superAdminBackup?.token) {
    throw new Error("Super Admin session is required to start impersonation.");
  }

  const decodedUser = decodeJwtPayload(tokens.access_token);
  const impersonatedUserId = Number(decodedUser?.sub);

  if (!Number.isFinite(impersonatedUserId)) {
    throw new Error("Invalid impersonation token payload.");
  }

  setImpersonationContext({
    tenantName,
    impersonatedUserId,
    superAdminAuthBackup: superAdminBackup,
    expiresAt: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
  });

  clearStoredSuperAdminAuth();

  setStoredAuth({
    token: tokens.access_token,
    refreshToken: "",
    user: decodedUser,
  });
};

export const endImpersonation = (): void => {
  const context = getImpersonationContext();

  clearStoredAuth();

  if (context?.superAdminAuthBackup) {
    setStoredSuperAdminAuth(context.superAdminAuthBackup);
  }

  clearImpersonationContext();
};

export const isImpersonationSession = (): boolean => {
  const stored = getStoredAuth();

  if (!stored?.token) {
    return false;
  }

  if (getImpersonationContext() !== null) {
    return true;
  }

  if (!stored.refreshToken) {
    return true;
  }

  const payload = decodeJwtPayload(stored.token);

  return payload?.impersonator_id != null;
};
